import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { getFileTypeIcon, createFallbackPlaceholder } from '../../../utils/videoUtils';

const VisualRepository = ({
  visuals = [],
  brands = [],
  categories = [],
  searchQuery,
  onSearchChange,
  onDeleteVisual,
  onUploadClick,
  onViewDetail,
  currentTab = 'active',
  currentUserRole = 'user'
}) => {
  const [viewMode, setViewMode] = useState('grid'); // grid, list, detail
  const [selectedVisual, setSelectedVisual] = useState(null);

  const getBrandName = (brandId) => {
    const brand = brands?.find(b => b?.id === brandId);
    return brand?.name || 'Unknown Brand';
  };

  const getBrandCode = (brandId) => {
    const brand = brands?.find(b => b?.id === brandId);
    return brand?.code || 'N/A';
  };

  const getCategoryName = (categoryId) => {
    const category = categories?.find(c => c?.id === categoryId);
    return category?.name || 'Unknown Category';
  };

  const getCategoryCode = (categoryId) => {
    const category = categories?.find(c => c?.id === categoryId);
    return category?.code || 'N/A';
  };

  const getFileTypeWithIcon = (visual) => {
    const fileType = visual?.fileType || 'unknown';
    const iconName = getFileTypeIcon(visual?.fileName || visual?.fileType || 'file');
    
    const modernIcons = {
      'image': 'Image',
      'video': 'Video', 
      'unknown': 'FileText'
    };
    
    return {
      modernIcon: modernIcons?.[fileType] || 'FileText',
      iconName: iconName,
      type: fileType
    };
  };

  const getThumbnailUrl = (visual) => {
    if (visual?.thumbnailUrl) {
      return visual?.thumbnailUrl;
    }
    return createFallbackPlaceholder(200, 150);
  };

  const checkAspectRatio = (visual) => {
    if (visual?.metaAspect && visual?.enteredAspect) {
      const tolerance = 0.01;
      const match = Math.abs(visual?.metaAspect - visual?.enteredAspect) <= tolerance;
      return {
        status: match ? 'OK' : 'MISMATCH',
        icon: match ? 'CheckCircle' : 'AlertTriangle',
        color: match ? 'text-green-600' : 'text-yellow-600'
      };
    }
    return { status: 'N/A', icon: null, color: 'text-muted-foreground' };
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-GB');
  };

  const viewModeOptions = [
    { value: 'grid', label: 'Grid View' },
    { value: 'list', label: 'List View' }
  ];

  const handleDetailView = (visual) => {
    setSelectedVisual(visual);
    setViewMode('detail');
  };

  const closeDetailView = () => {
    setSelectedVisual(null);
    setViewMode('grid');
  };

  // Get button label and icon based on current tab and permissions - ENHANCED
  const getActionButtonConfig = () => {
    if (currentTab === 'active') {
      return {
        label: 'Replace & Archive',
        icon: 'GitBranch',
        className: 'text-muted-foreground hover:text-yellow-600',
        tooltip: 'Check dependencies and replace KVs before archiving'
      };
    } else {
      // Archived tab - Admin-only permanent deletion
      if (currentUserRole === 'admin') {
        return {
          label: 'Delete Permanently',
          icon: 'Trash2',
          className: 'text-muted-foreground hover:text-destructive',
          tooltip: 'Permanently delete (Admin only)'
        };
      } else {
        return null; // No delete button for non-admin users in archive
      }
    }
  };

  // Enhanced Detail View with improved archive information and deletion policy
  const DetailView = ({ visual, onClose }) => (
    <div className="bg-card rounded-lg border border-border shadow-sm">
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
              Visual Details
            </h3>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              iconName="Grid3x3"
              onClick={() => setViewMode('grid')}
            >
              Back to Grid
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
            >
              <Icon name="X" size={16} />
            </Button>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Full-size preview */}
        <div className="flex justify-center">
          <div className="relative max-w-2xl">
            <img 
              src={getThumbnailUrl(visual)} 
              alt={visual?.title} 
              className="w-full rounded-lg border border-border object-contain max-h-96"
            />
            {visual?.fileType === 'video' && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-16 h-16 bg-black/50 rounded-full flex items-center justify-center">
                  <Icon name="Play" size={24} className="text-white ml-1" />
                </div>
              </div>
            )}
            {visual?.kvStatus === 'Archived' && (
              <div className="absolute top-3 left-3 bg-yellow-500/90 text-white px-2 py-1 rounded text-sm flex items-center gap-1">
                <Icon name="Archive" size={12} />
                Archived
              </div>
            )}
          </div>
        </div>

        {/* Enhanced Archive Information */}
        {visual?.kvStatus === 'Archived' && (
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <h4 className="text-md font-semibold text-yellow-800 border-b border-yellow-200 pb-2 mb-3">
              Archive Information
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mb-4">
              <div className="flex justify-between">
                <span className="text-yellow-700">Archived On:</span>
                <span className="font-medium text-yellow-800">{formatDate(visual?.archivedDate)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-yellow-700">Archived By:</span>
                <span className="font-medium text-yellow-800">{visual?.archivedBy}</span>
              </div>
            </div>
          </div>
        )}

        {/* Metadata sections */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h4 className="text-md font-semibold text-foreground border-b border-border pb-2">
              Basic Information
            </h4>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">KV ID:</span>
                <span className="font-mono">{visual?.kvId}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Status:</span>
                <span className={`font-medium ${
                  visual?.kvStatus === 'Active' ? 'text-green-600' : 
                  visual?.kvStatus === 'Archived'? 'text-yellow-600' : 'text-red-600'
                }`}>
                  {visual?.kvStatus}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Title:</span>
                <span className="font-medium">{visual?.title}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Brand:</span>
                <span>{getBrandName(visual?.brandId)} ({getBrandCode(visual?.brandId)})</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Category:</span>
                <span>{getCategoryName(visual?.categoryId)} ({getCategoryCode(visual?.categoryId)})</span>
              </div>
            </div>
          </div>

          {/* File Information */}
          <div className="space-y-4">
            <h4 className="text-md font-semibold text-foreground border-b border-border pb-2">
              File Information
            </h4>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">File Type:</span>
                <span className="flex items-center gap-2">
                  <Icon name={getFileTypeWithIcon(visual)?.modernIcon} size={16} />
                  {visual?.fileExtension || visual?.fileType?.toUpperCase()}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">File Size:</span>
                <span className="font-medium">{visual?.fileSizeFormatted || visual?.fileSize || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Pixel Dimensions:</span>
                <span>{visual?.metaWidthPx || 'N/A'} × {visual?.metaHeightPx || 'N/A'}</span>
              </div>
              {visual?.durationDisplay && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Duration:</span>
                  <span className="flex items-center gap-1">
                    <Icon name="Play" size={12} />
                    {visual?.durationDisplay}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Physical Dimensions */}
          {(visual?.widthFt && visual?.heightFt) && (
            <div className="space-y-4">
              <h4 className="text-md font-semibold text-foreground border-b border-border pb-2">
                Physical Dimensions
              </h4>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Entered Dimensions:</span>
                  <span>{visual?.widthFt} ft × {visual?.heightFt} ft</span>
                </div>
                {visual?.areaSqft && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Area:</span>
                    <span>{visual?.areaSqft} sqft</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Aspect Ratio Check:</span>
                  <span className={`font-medium flex items-center gap-1 ${checkAspectRatio(visual)?.color}`}>
                    {checkAspectRatio(visual)?.icon && (
                      <Icon name={checkAspectRatio(visual)?.icon} size={12} />
                    )}
                    {checkAspectRatio(visual)?.status}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Usage Information */}
          <div className="space-y-4">
            <h4 className="text-md font-semibold text-foreground border-b border-border pb-2">
              Usage Information
            </h4>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Usage Count:</span>
                <span className="font-medium">{visual?.usageCount || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Upload Date:</span>
                <span>{formatDate(visual?.uploadDate)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Branding Type:</span>
                <span>{visual?.brandingType}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Usage History */}
        {visual?.usedIn?.length > 0 && (
          <div className="space-y-4">
            <h4 className="text-md font-semibold text-foreground border-b border-border pb-2">
              Usage History
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {visual?.usedIn?.map((usage, index) => (
                <div key={index} className="flex items-center gap-2 text-sm bg-accent/10 p-2 rounded">
                  <Icon name="MapPin" size={12} className="text-muted-foreground" />
                  <span className="text-foreground">{usage}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Enhanced Actions section with simplified messaging */}
        <div className="flex items-center justify-between pt-6 border-t border-border">
          <div className="text-sm text-muted-foreground">
            {visual?.kvStatus === 'Archived' && currentUserRole !== 'admin' && (
              <span className="flex items-center gap-1">
                <Icon name="Info" size={14} />
                Only Admin users can delete archived KVs
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-3">
            {(() => {
              const actionConfig = getActionButtonConfig();
              if (actionConfig) {
                return (
                  <Button
                    variant={visual?.kvStatus === 'Archived' ? 'destructive' : 'outline'}
                    onClick={() => onDeleteVisual(visual)}
                    iconName={actionConfig?.icon}
                    iconPosition="left"
                    className={visual?.kvStatus === 'Archived' ? '' : actionConfig?.className}
                    title={actionConfig?.tooltip}
                  >
                    {actionConfig?.label}
                  </Button>
                );
              }
              return null;
            })()}
          </div>
        </div>
      </div>
    </div>
  );

  // Show detail view if selected
  if (viewMode === 'detail' && selectedVisual) {
    return <DetailView visual={selectedVisual} onClose={closeDetailView} />;
  }

  return (
    <div className="bg-card rounded-lg border border-border shadow-sm">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
              <Icon name={currentTab === 'active' ? 'Database' : 'Archive'} size={20} className="text-accent" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                {currentTab === 'active' ? 'Active' : 'Archived'} Visual Repository
              </h3>
              <p className="text-sm text-muted-foreground">
                {visuals?.length} {currentTab} visual{visuals?.length !== 1 ? 's' : ''} available
                {currentTab === 'archived' && currentUserRole !== 'admin' && (
                  <span className="ml-2 text-yellow-600">• Admin required for permanent deletion</span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Select
              options={viewModeOptions}
              value={viewMode}
              onChange={setViewMode}
              placeholder="View Mode"
            />
            {currentTab === 'active' && (
              <Button
                variant="default"
                iconName="Upload"
                iconPosition="left"
                onClick={onUploadClick}
              >
                Upload Visual
              </Button>
            )}
          </div>
        </div>

        {/* Search */}
        <Input
          type="text"
          placeholder={`Search ${currentTab} visuals by KV ID, title, brand, or category...`}
          value={searchQuery}
          onChange={(e) => onSearchChange(e?.target?.value)}
          iconName="Search"
          iconPosition="left"
        />
      </div>
      {/* Content */}
      <div className="p-4">
        {visuals?.length === 0 ? (
          <div className="text-center py-12">
            <Icon 
              name={currentTab === 'active' ? 'Image' : 'Archive'} 
              size={48} 
              className="text-muted-foreground mx-auto mb-4" 
            />
            <h3 className="text-lg font-medium text-foreground mb-2">
              No {currentTab} visuals found
            </h3>
            <p className="text-muted-foreground mb-6">
              {searchQuery 
                ? `No ${currentTab} visuals match your search criteria.` 
                : currentTab === 'active' ?'Upload your first key visual to get started.' :'No visuals have been archived yet.'
              }
            </p>
            {currentTab === 'active' && (
              <Button
                variant="default"
                iconName="Upload"
                iconPosition="left"
                onClick={onUploadClick}
              >
                Upload Visual
              </Button>
            )}
          </div>
        ) : viewMode === 'grid' ? (
          /* Enhanced Grid View with Replace & Archive indicators */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {visuals?.map(visual => (
              <div
                key={visual?.id}
                className={`border border-border rounded-lg overflow-hidden hover:shadow-md transition-shadow cursor-pointer ${
                  visual?.kvStatus === 'Archived' ? 'ring-2 ring-yellow-200' : 'ring-1 ring-orange-200'
                }`}
                onClick={() => handleDetailView(visual)}
              >
                {/* Thumbnail */}
                <div className="relative h-48 bg-gray-50">
                  <img 
                    src={getThumbnailUrl(visual)} 
                    alt={visual?.title} 
                    className="w-full h-full object-cover"
                  />
                  {visual?.fileType === 'video' && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-12 h-12 bg-black/50 rounded-full flex items-center justify-center">
                        <Icon name="Play" size={16} className="text-white ml-0.5" />
                      </div>
                    </div>
                  )}
                  <div className="absolute top-2 right-2 bg-black/70 text-white px-2 py-1 rounded text-xs flex items-center gap-1">
                    <Icon name={getFileTypeWithIcon(visual)?.modernIcon} size={12} className="text-white" />
                    <span>{visual?.fileExtension || visual?.fileType?.toUpperCase()}</span>
                  </div>
                  {visual?.kvStatus === 'Archived' && (
                    <div className="absolute top-2 left-2 bg-yellow-500/90 text-white px-2 py-1 rounded text-xs flex items-center gap-1">
                      <Icon name="Archive" size={10} />
                      Archived
                    </div>
                  )}
                </div>

                {/* Enhanced Card Content */}
                <div className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h4 className="font-medium text-foreground text-sm">{visual?.title}</h4>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded font-mono">
                          {visual?.kvId}
                        </span>
                        <span className={`px-2 py-1 text-xs rounded flex items-center gap-1 ${
                          visual?.kvStatus === 'Active' ?'bg-green-100 text-green-700' :'bg-yellow-100 text-yellow-700'
                        }`}>
                          <Icon name={visual?.kvStatus === 'Active' ? 'Eye' : 'Archive'} size={8} />
                          {visual?.kvStatus}
                        </span>
                      </div>
                    </div>
                    {(() => {
                      const actionConfig = getActionButtonConfig();
                      return actionConfig ? (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e?.stopPropagation();
                            onDeleteVisual(visual);
                          }}
                          className={`${actionConfig?.className} ${
                            visual?.kvStatus === 'Archived' ? 'hover:bg-destructive/10' : 'hover:bg-orange/10'
                          }`}
                          title={actionConfig?.tooltip}
                        >
                          <Icon name={actionConfig?.icon} size={14} />
                        </Button>
                      ) : null;
                    })()}
                  </div>

                  <div className="space-y-1 text-xs text-muted-foreground">
                    <div>{getBrandName(visual?.brandId)} ({getBrandCode(visual?.brandId)})</div>
                    <div>{getCategoryName(visual?.categoryId)} ({getCategoryCode(visual?.categoryId)})</div>
                    
                    <div className="flex items-center justify-between pt-1">
                      <span className="flex items-center gap-1">
                        <Icon name={getFileTypeWithIcon(visual)?.modernIcon} size={12} />
                        {visual?.fileSizeFormatted || visual?.fileSize || 'N/A'}
                      </span>
                      {checkAspectRatio(visual)?.status !== 'N/A' && (
                        <span className={`flex items-center gap-1 ${checkAspectRatio(visual)?.color}`}>
                          <Icon name={checkAspectRatio(visual)?.icon} size={10} />
                          {checkAspectRatio(visual)?.status}
                        </span>
                      )}
                    </div>

                    {visual?.widthFt && visual?.heightFt && (
                      <div className="text-foreground font-medium">
                        {visual?.widthFt} ft × {visual?.heightFt} ft
                        {visual?.areaSqft && <span className="text-muted-foreground ml-1">({visual?.areaSqft} sqft)</span>}
                      </div>
                    )}

                    {visual?.durationDisplay && (
                      <div className="flex items-center gap-1 text-primary">
                        <Icon name="Play" size={10} />
                        {visual?.durationDisplay}
                      </div>
                    )}

                    {/* Simplified status info for Active KVs */}
                    {visual?.kvStatus === 'Active' && visual?.usageCount > 0 && (
                      <div className="flex items-center gap-1 text-orange-600 pt-1 text-xs">
                        <Icon name="AlertTriangle" size={10} />
                        <span>Dependencies: {visual?.usageCount}</span>
                      </div>
                    )}

                    {/* Simplified archive info */}
                    {visual?.kvStatus === 'Archived' && visual?.archivedDate && (
                      <div className="flex items-center gap-1 text-yellow-600 pt-1 text-xs">
                        <Icon name="Calendar" size={10} />
                        <span>Archived: {formatDate(visual?.archivedDate)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Enhanced List View with Replace & Archive indicators */
          <div className="space-y-2">
            {visuals?.map(visual => (
              <div
                key={visual?.id}
                className={`flex items-center gap-4 p-4 border rounded-lg hover:shadow-sm transition-shadow cursor-pointer ${
                  visual?.kvStatus === 'Active' ?'bg-orange-50/50 border-orange-200' 
                    : visual?.kvStatus === 'Archived' ?'bg-yellow-50/50 border-yellow-200' :'border-border'
                }`}
                onClick={() => handleDetailView(visual)}
              >
                {/* Small Thumbnail */}
                <div className="relative w-16 h-12 bg-gray-50 rounded overflow-hidden flex-shrink-0">
                  <img 
                    src={getThumbnailUrl(visual)} 
                    alt={visual?.title} 
                    className="w-full h-full object-cover"
                  />
                  {visual?.fileType === 'video' && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-6 h-6 bg-black/50 rounded-full flex items-center justify-center">
                        <Icon name="Play" size={8} className="text-white ml-0.5" />
                      </div>
                    </div>
                  )}
                  {visual?.kvStatus === 'Archived' && (
                    <div className="absolute top-0 right-0 bg-yellow-500/90 text-white p-0.5 rounded-bl text-xs">
                      <Icon name="Archive" size={8} />
                    </div>
                  )}
                </div>

                {/* Enhanced Content */}
                <div className="flex-1 grid grid-cols-8 gap-4 items-center text-sm">
                  <div className="col-span-2">
                    <div className="font-medium text-foreground">{visual?.title}</div>
                    <div className="text-xs text-primary font-mono">{visual?.kvId}</div>
                    <div className={`text-xs mt-1 flex items-center gap-1 ${
                      visual?.kvStatus === 'Active' ? 'text-green-600' : 'text-yellow-600'
                    }`}>
                      <span>{visual?.kvStatus}</span>
                      {visual?.kvStatus === 'Active' && visual?.usageCount > 0 && (
                        <Icon name="AlertTriangle" size={10} className="text-orange-600" title="Has dependencies" />
                      )}
                    </div>
                  </div>
                  <div>{getBrandName(visual?.brandId)} ({getBrandCode(visual?.brandId)})</div>
                  <div>{getCategoryName(visual?.categoryId)} ({getCategoryCode(visual?.categoryId)})</div>
                  <div className="flex items-center gap-2">
                    <Icon name={getFileTypeWithIcon(visual)?.modernIcon} size={14} />
                    <span className="text-xs">{visual?.fileSizeFormatted || visual?.fileSize || 'N/A'}</span>
                  </div>
                  <div>
                    {visual?.widthFt && visual?.heightFt ? (
                      <span>{visual?.widthFt} × {visual?.heightFt} ft</span>
                    ) : (
                      <span className="text-muted-foreground">N/A</span>
                    )}
                    {visual?.durationDisplay && (
                      <div className="text-xs text-primary flex items-center gap-1">
                        <Icon name="Play" size={10} />
                        {visual?.durationDisplay}
                      </div>
                    )}
                  </div>
                  <div className="text-xs">
                    {visual?.kvStatus === 'Active' && visual?.usageCount > 0 ? (
                      <div className="text-orange-600">
                        <div>Dependencies: {visual?.usageCount}</div>
                      </div>
                    ) : visual?.kvStatus === 'Archived' && visual?.archivedDate ? (
                      <div className="text-yellow-600">
                        <div>Archived: {formatDate(visual?.archivedDate)}</div>
                        <div>By: {visual?.archivedBy}</div>
                      </div>
                    ) : (
                      <div>Usage: {visual?.usageCount || 0}</div>
                    )}
                  </div>
                  <div className="flex items-center justify-end gap-2">
                    {checkAspectRatio(visual)?.status !== 'N/A' && (
                      <span className={`text-xs flex items-center gap-1 ${checkAspectRatio(visual)?.color}`}>
                        <Icon name={checkAspectRatio(visual)?.icon} size={10} />
                        {checkAspectRatio(visual)?.status}
                      </span>
                    )}
                    {(() => {
                      const actionConfig = getActionButtonConfig();
                      return actionConfig ? (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e?.stopPropagation();
                            onDeleteVisual(visual);
                          }}
                          className={`${actionConfig?.className} ${
                            visual?.kvStatus === 'Archived' ? 'hover:bg-destructive/10' : ''
                          }`}
                          title={actionConfig?.tooltip}
                        >
                          <Icon name={actionConfig?.icon} size={14} />
                        </Button>
                      ) : null;
                    })()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default VisualRepository;